import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { Download, Eye, FileText, TestTube, Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import VisualizadorArquivo from "./visualizadorArquivo";

interface Exame {
  id: number;
  id_exame: number;
  status_exame: string;
  data_solicitacao: string;
  data_realizacao?: string;
  exame?: {
    id: number;
    nome: string;
  };
}

interface Documento {
  id: number;
  nome_arquivo: string;
  caminho_arquivo: string;
  tipo_arquivo?: string;
  data_upload: string;
}

interface ExamesEDocumentosProps {
  atendimentoId?: number;
}

export const ExamesEDocumentos = ({ atendimentoId }: ExamesEDocumentosProps) => {
  const [exames, setExames] = useState<Exame[]>([]);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [arquivoSelecionado, setArquivoSelecionado] = useState<{
    url: string;
    nome: string;
    tipo?: string;
  } | null>(null);

  const loadExamesEDocumentos = async () => {
    if (!atendimentoId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/atendimentos/${atendimentoId}/exames-documentos`);
      if (response?.result) {
        setExames(response.result.exames || []);
        setDocumentos(response.result.documentos || []);
      }
    } catch (error) {
      console.error('Erro ao carregar exames e documentos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os exames e documentos.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (atendimentoId) {
      loadExamesEDocumentos();
    }
  }, [atendimentoId]);

  // Escutar evento de atualização de exames quando formulários são salvos
  useEffect(() => {
    const handleExamesAtualizados = (event: CustomEvent) => {
      const { atendimentoId: eventAtendimentoId } = event.detail || {};
      if (!eventAtendimentoId || eventAtendimentoId === atendimentoId) {
        console.log('🔄 [ExamesEDocumentos] Recarregando exames após salvamento de formulário');
        loadExamesEDocumentos();
      }
    };

    window.addEventListener('examesAtualizados', handleExamesAtualizados as EventListener);
    return () => {
      window.removeEventListener('examesAtualizados', handleExamesAtualizados as EventListener);
    };
  }, [atendimentoId, loadExamesEDocumentos]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SOLICITADO':
        return <Badge variant="secondary">Solicitado</Badge>;
      case 'REALIZADO':
        return <Badge variant="default" className="bg-blue-500">Realizado</Badge>;
      case 'ANEXADO':
        return <Badge variant="default" className="bg-green-500">Anexado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getFileUrl = (documento: Documento): string => {
    const BASE_URL = import.meta.env.VITE_API_BASE_URL;
    const caminhoEncoded = encodeURIComponent(documento.caminho_arquivo);
    return `${BASE_URL}/api/upload/download/${caminhoEncoded}`;
  };

  const downloadFile = async (documento: Documento) => {
    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const token = localStorage.getItem('auth_token');
      const caminhoEncoded = encodeURIComponent(documento.caminho_arquivo);
      
      const response = await fetch(
        `${BASE_URL}/api/upload/download/${caminhoEncoded}`,
        {
          method: 'GET',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documento.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso",
        description: `Arquivo "${documento.nome_arquivo}" baixado com sucesso!`,
      });
    } catch (error: any) {
      console.error('Erro ao baixar arquivo:', error);
      toast({
        title: "Erro",
        description: error?.message || "Não foi possível baixar o arquivo.",
        variant: "destructive"
      });
    }
  };

  const visualizarArquivo = async (documento: Documento) => {
    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const token = localStorage.getItem('auth_token');
      const caminhoEncoded = encodeURIComponent(documento.caminho_arquivo);
      
      const response = await fetch(
        `${BASE_URL}/api/upload/download/${caminhoEncoded}`,
        {
          method: 'GET',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao carregar arquivo para visualização');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      setArquivoSelecionado({
        url,
        nome: documento.nome_arquivo,
        tipo: documento.tipo_arquivo
      });
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao visualizar arquivo:', error);
      toast({
        title: "Erro",
        description: "Não foi possível abrir o arquivo para visualização.",
        variant: "destructive"
      });
    }
  };

  const fecharVisualizador = () => {
    if (arquivoSelecionado?.url) {
      window.URL.revokeObjectURL(arquivoSelecionado.url);
    }
    setVisualizadorAberto(false);
    setArquivoSelecionado(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    if (!atendimentoId) {
      toast({
        title: "Aviso",
        description: "Salve o atendimento antes de anexar documentos.",
        variant: "destructive"
      });
      return;
    }

    // Fazer upload de cada arquivo
    for (const file of selectedFiles) {
      await uploadFile(file);
    }

    // Limpar input
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    
    if (!atendimentoId) {
      toast({
        title: "Aviso",
        description: "Salve o atendimento antes de anexar documentos.",
        variant: "destructive"
      });
      return;
    }

    // Fazer upload de cada arquivo
    for (const file of droppedFiles) {
      await uploadFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const uploadFile = async (file: File) => {
    if (!atendimentoId) return;

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);

      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const token = localStorage.getItem('auth_token');

      const response = await fetch(
        `${BASE_URL}/api/atendimentos/${atendimentoId}/documentos`,
        {
          method: 'POST',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.message || error?.mensagem || 'Erro ao fazer upload');
      }

      const result = await response.json();
      
      // Recarregar lista de documentos
      await loadExamesEDocumentos();
      
      toast({
        title: "Sucesso",
        description: `Arquivo "${file.name}" anexado com sucesso!`,
      });
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      toast({
        title: "Erro",
        description: error?.message || "Não foi possível anexar o arquivo.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const removerDocumento = async (documentoId: number, nomeArquivo: string) => {
    if (!atendimentoId) return;

    try {
      setLoading(true);
      await api.delete(`/api/atendimentos/${atendimentoId}/documentos/${documentoId}`);
      
      // Recarregar lista
      await loadExamesEDocumentos();
      
      toast({
        title: "Sucesso",
        description: `Arquivo "${nomeArquivo}" removido com sucesso!`,
      });
    } catch (error: any) {
      console.error('Erro ao remover documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível remover o arquivo.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TestTube className="h-5 w-5" />
          Exames e Documentos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Carregando...
          </div>
        ) : (
          <>
            {/* Seção de Exames */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <TestTube className="h-4 w-4" />
                Exames Solicitados ({exames.length})
              </h3>
              {exames.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum exame solicitado</p>
              ) : (
                <div className="space-y-2">
                  {exames.map((exame) => (
                    <div
                      key={exame.id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-background"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {exame.exame?.nome || `Exame #${exame.id_exame}`}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusBadge(exame.status_exame)}
                          <span className="text-xs text-muted-foreground">
                            {new Date(exame.data_solicitacao).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Seção de Documentos */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documentos Anexados ({documentos.length})
              </h3>

              {/* Área de Upload */}
              {atendimentoId ? (
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center hover:bg-accent/50 cursor-pointer transition-colors mb-4 ${
                    uploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <label htmlFor="file-upload-atendimento" className="cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm font-medium">
                      {uploading ? 'Enviando...' : 'Arraste arquivos ou clique para enviar'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, JPG, PNG (máx 10MB)
                    </p>
                  </label>
                  <input
                    id="file-upload-atendimento"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={uploading || !atendimentoId}
                  />
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg p-6 text-center bg-muted/50 mb-4">
                  <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-sm font-medium text-muted-foreground">
                    Salve o atendimento antes de anexar documentos
                  </p>
                </div>
              )}

              {documentos.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum documento anexado</p>
              ) : (
                <div className="space-y-2">
                  {documentos.map((documento) => (
                    <div
                      key={documento.id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-background"
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{documento.nome_arquivo}</p>
                          <p className="text-xs text-muted-foreground">
                            {documento.tipo_arquivo?.toUpperCase()} • {new Date(documento.data_upload).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {(documento.tipo_arquivo?.toLowerCase() === 'pdf' || 
                            ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(documento.tipo_arquivo?.toLowerCase() || '')) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => visualizarArquivo(documento)}
                              className="h-8 w-8 p-0"
                              title="Visualizar"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadFile(documento)}
                            className="h-8 w-8 p-0"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removerDocumento(documento.id, documento.nome_arquivo)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            disabled={loading}
                            title="Remover"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Visualizador de Arquivo */}
      {arquivoSelecionado && (
        <VisualizadorArquivo
          arquivoUrl={arquivoSelecionado.url}
          nomeArquivo={arquivoSelecionado.nome}
          tipoArquivo={arquivoSelecionado.tipo}
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </Card>
  );
};

